import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { TransportMode } from 'src/cqrs/client';
import { connectOneServiceToStore } from '~services/oneService';
import {
  loadPersisted,
  readLegacyZustand,
  savePersisted,
} from '~utils/persistedState';

// Re-export TransportMode so existing imports can switch to this module.
export type { TransportMode };

type ColumnOptions = {
  capabilities: boolean;
  deviceType: boolean;
  manufacturer: boolean;
};

export type OptionState = {
  locale: 'en' | 'fi';
  shownColumns: ColumnOptions;
  transportMode: TransportMode;
};

const OPTION_STORAGE_KEY = 'options';

const TRANSPORT_MODES: readonly TransportMode[] = ['auto', 'signalr', 'http'];

function normalizeTransportMode(value: unknown): TransportMode {
  if (value === 'socket') return 'signalr';
  return TRANSPORT_MODES.includes(value as TransportMode)
    ? (value as TransportMode)
    : 'auto';
}

const defaultState: OptionState = {
  locale: 'en',
  shownColumns: {
    capabilities: true,
    deviceType: true,
    manufacturer: true,
  },
  transportMode: 'auto',
};

function loadInitialState(): OptionState {
  const persisted = loadPersisted(
    OPTION_STORAGE_KEY,
    readLegacyZustand('option-store', defaultState)
  );
  return {
    ...persisted,
    transportMode: normalizeTransportMode(persisted.transportMode),
  };
}

type OptionsContextValue = OptionState & {
  setLocale: (locale: 'en' | 'fi') => void;
  setShownColumns: (columns: ColumnOptions) => void;
  setTransportMode: (mode: TransportMode) => void;
};

const OptionsContext = createContext<OptionsContextValue | undefined>(
  undefined
);

function persist(state: OptionState) {
  savePersisted(OPTION_STORAGE_KEY, state);
}

export function OptionsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OptionState>(loadInitialState);

  // Feed the transport preference into the OneService singleton so its transport
  // selection (SignalR ladder vs HTTP) actually follows the user's choice. Wire
  // a getter once; a ref keeps it reading the latest value without re-wiring.
  const transportModeRef = useRef(state.transportMode);
  transportModeRef.current = state.transportMode;
  useEffect(() => {
    connectOneServiceToStore(() => transportModeRef.current);
  }, []);

  const setLocale = useCallback((locale: 'en' | 'fi') => {
    setState(prev => {
      const next = { ...prev, locale };
      persist(next);
      return next;
    });
  }, []);

  const setShownColumns = useCallback((shownColumns: ColumnOptions) => {
    setState(prev => {
      const next = { ...prev, shownColumns };
      persist(next);
      return next;
    });
  }, []);

  const setTransportMode = useCallback((transportMode: TransportMode) => {
    setState(prev => {
      const next = { ...prev, transportMode };
      persist(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ ...state, setLocale, setShownColumns, setTransportMode }),
    [state, setLocale, setShownColumns, setTransportMode]
  );

  return (
    <OptionsContext.Provider value={value}>{children}</OptionsContext.Provider>
  );
}

export function useOptions(): OptionsContextValue {
  const ctx = useContext(OptionsContext);
  if (!ctx) throw new Error('useOptions must be used within OptionsProvider');
  return ctx;
}
